import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  LayoutDashboard, MonitorSmartphone, ListVideo, Settings,
  Plus, Trash2, CheckCircle, XCircle, Clock, DollarSign,
  Copy, Eye, RefreshCw, LogOut, Search, MoreVertical,
  Tv, Film, Radio, Loader2, Megaphone
} from "lucide-react";
import axios from "axios";
import AdminContentTab from "../components/AdminContentTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple auth for admin
const getAuthHeader = () => {
  const credentials = localStorage.getItem("adminCredentials");
  if (credentials) {
    return { Authorization: `Basic ${credentials}` };
  }
  return {};
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  
  // Data
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [tickers, setTickers] = useState([]);
  const [showTickerModal, setShowTickerModal] = useState(false);
  const [tickerForm, setTickerForm] = useState({ message: "", target_audience: "all", priority: 0, is_active: true, expires_at: "" });
  const [playlistForm, setPlaylistForm] = useState({
    device_key: "",
    name: "",
    playlist_type: "xtream",
    m3u_url: "",
    xtream_url: "",
    xtream_username: "",
    xtream_password: ""
  });

  useEffect(() => {
    const credentials = localStorage.getItem("adminCredentials");
    if (credentials) {
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const credentials = btoa(`${username}:${password}`);
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      
      localStorage.setItem("adminCredentials", credentials);
      setIsAuthenticated(true);
      setStats(response.data);
      fetchDevices();
      toast.success("Welcome to Admin Panel");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminCredentials");
    setIsAuthenticated(false);
    setUsername("");
    setPassword("");
  };

  const fetchData = async () => {
    await Promise.all([fetchStats(), fetchDevices()]);
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API}/admin/devices`, {
        headers: getAuthHeader()
      });
      setDevices(response.data);
    } catch (error) {
      console.error("Failed to fetch devices");
    }
  };

  const activateDevice = async (deviceKey) => {
    try {
      const response = await axios.post(
        `${API}/admin/device/${deviceKey}/activate`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Activation code: ${response.data.activation_code}`);
      fetchDevices();
    } catch (error) {
      toast.error("Failed to generate activation code");
    }
  };

  const markAsPaid = async (deviceKey) => {
    try {
      await axios.patch(
        `${API}/admin/device/${deviceKey}`,
        { is_paid: true },
        { headers: getAuthHeader() }
      );
      toast.success("Device marked as paid");
      fetchDevices();
    } catch (error) {
      toast.error("Failed to update device");
    }
  };

  const deleteDevice = async (deviceKey) => {
    if (!window.confirm("Are you sure you want to delete this device?")) return;
    
    try {
      await axios.delete(`${API}/admin/device/${deviceKey}`, {
        headers: getAuthHeader()
      });
      toast.success("Device deleted");
      fetchDevices();
      setSelectedDevice(null);
    } catch (error) {
      toast.error("Failed to delete device");
    }
  };

  const viewDeviceDetails = async (deviceKey) => {
    try {
      const response = await axios.get(`${API}/admin/device/${deviceKey}`, {
        headers: getAuthHeader()
      });
      setSelectedDevice(response.data);
    } catch (error) {
      toast.error("Failed to fetch device details");
    }
  };

  const createPlaylist = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/playlist`, playlistForm, {
        headers: getAuthHeader()
      });
      toast.success("Playlist created");
      setShowPlaylistModal(false);
      setPlaylistForm({
        device_key: "",
        name: "",
        playlist_type: "xtream",
        m3u_url: "",
        xtream_url: "",
        xtream_username: "",
        xtream_password: ""
      });
      if (selectedDevice) {
        viewDeviceDetails(selectedDevice.device_key);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create playlist");
    } finally {
      setLoading(false);
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm("Delete this playlist?")) return;
    
    try {
      await axios.delete(`${API}/admin/playlist/${playlistId}`, {
        headers: getAuthHeader()
      });
      toast.success("Playlist deleted");
      if (selectedDevice) {
        viewDeviceDetails(selectedDevice.device_key);
      }
    } catch (error) {
      toast.error("Failed to delete playlist");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getStatusBadge = (device) => {
    const status = device.calculated_status || device.status;
    switch (status) {
      case "active":
        return <span className="badge-active px-2 py-1 rounded text-xs">Active</span>;
      case "trial":
        return <span className="badge-trial px-2 py-1 rounded text-xs">Trial ({device.days_remaining}d)</span>;
      case "expired":
        return <span className="badge-expired px-2 py-1 rounded text-xs">Expired</span>;
      default:
        return <span className="badge-pending px-2 py-1 rounded text-xs">Pending</span>;
    }
  };

  const fetchTickers = async () => {
    try {
      const res = await axios.get(`${API}/admin/tickers`, { headers: getAuthHeader() });
      setTickers(res.data);
    } catch(e) { console.error("Failed to fetch tickers"); }
  };

  const filteredDevices = devices.filter(d => 
    d.device_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.device_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050507] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#0F0F13] border-white/5">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center mb-4">
              <LayoutDashboard className="w-6 h-6 text-blue-500" />
            </div>
            <CardTitle className="text-xl text-white">Admin Portal</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-zinc-400">Username</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="admin"
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500"
                data-testid="admin-login-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] text-white">
      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar p-4" data-testid="admin-sidebar">
          <div className="flex items-center gap-2 mb-8">
            <Tv className="w-6 h-6 text-blue-500" />
            <span className="font-semibold">IPTV Admin</span>
          </div>

          <nav className="space-y-1">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`nav-item w-full ${activeTab === "dashboard" ? "active" : ""}`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("devices")}
              className={`nav-item w-full ${activeTab === "devices" ? "active" : ""}`}
              data-testid="nav-devices"
            >
              <MonitorSmartphone className="w-5 h-5" />
              Devices
            </button>
            <button
              onClick={() => setActiveTab("playlists")}
              className={`nav-item w-full ${activeTab === "playlists" ? "active" : ""}`}
              data-testid="nav-playlists"
            >
              <ListVideo className="w-5 h-5" />
              Playlists
            </button>
            <button
              onClick={() => { setActiveTab("broadcasts"); fetchTickers(); }}
              className={`nav-item w-full ${activeTab === "broadcasts" ? "active" : ""}`}
              data-testid="nav-broadcasts"
            >
              <Tv className="w-5 h-5" />
              Broadcasts
            </button>
            <button
              onClick={() => setActiveTab("content")}
              className={`nav-item w-full ${activeTab === "content" ? "active" : ""}`}
              data-testid="nav-content"
            >
              <Megaphone className="w-5 h-5" />
              Content &amp; Stats
            </button>
          </nav>

          <div className="absolute bottom-4 left-4 right-4">
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-zinc-500 hover:text-white"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-semibold">
              {activeTab === "dashboard" && "Dashboard"}
              {activeTab === "devices" && "Devices"}
              {activeTab === "playlists" && "Playlists"}
              {activeTab === "broadcasts" && "Broadcasts"}
              {activeTab === "content" && "Content & Stats"}
            </h1>
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="border-white/10 text-zinc-400 hover:text-white"
              data-testid="refresh-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === "dashboard" && stats && (
            <div className="space-y-8 animate-fade-in">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="stat-card" data-testid="stat-total-devices">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Total Devices</p>
                      <p className="text-3xl font-semibold mt-1">{stats.total_devices}</p>
                    </div>
                    <MonitorSmartphone className="w-8 h-8 text-blue-500 opacity-50" />
                  </div>
                </div>
                <div className="stat-card" data-testid="stat-active-devices">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Active Devices</p>
                      <p className="text-3xl font-semibold mt-1 text-green-500">{stats.active_devices}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
                  </div>
                </div>
                <div className="stat-card" data-testid="stat-paid-devices">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Paid Devices</p>
                      <p className="text-3xl font-semibold mt-1 text-yellow-500">{stats.paid_devices}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-yellow-500 opacity-50" />
                  </div>
                </div>
                <div className="stat-card" data-testid="stat-expired-devices">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-zinc-500 text-sm">Expired</p>
                      <p className="text-3xl font-semibold mt-1 text-red-500">{stats.expired_devices}</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-500 opacity-50" />
                  </div>
                </div>
              </div>

              {/* Recent Devices */}
              <div>
                <h2 className="text-lg font-medium mb-4">Recent Devices</h2>
                <div className="bg-[#0F0F13] rounded-xl border border-white/5 overflow-hidden">
                  <table className="admin-table">
                    <thead>
                      <tr className="text-zinc-500 text-sm">
                        <th>Device Key</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devices.slice(0, 5).map((device) => (
                        <tr key={device.device_key}>
                          <td className="font-mono">{device.device_key}</td>
                          <td>{getStatusBadge(device)}</td>
                          <td className="text-zinc-500 text-sm">
                            {new Date(device.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setActiveTab("devices");
                                viewDeviceDetails(device.device_key);
                              }}
                              className="text-zinc-400 hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Devices Tab */}
          {activeTab === "devices" && (
            <div className="space-y-6 animate-fade-in">
              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Search devices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#0F0F13] border-white/10 text-white"
                  data-testid="device-search-input"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Device List */}
                <div className="lg:col-span-2">
                  <div className="bg-[#0F0F13] rounded-xl border border-white/5 overflow-hidden">
                    <table className="admin-table">
                      <thead>
                        <tr className="text-zinc-500 text-sm">
                          <th>Device Key</th>
                          <th>Activation Code</th>
                          <th>Status</th>
                          <th>Paid</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDevices.map((device) => (
                          <tr 
                            key={device.device_key}
                            className={selectedDevice?.device_key === device.device_key ? "bg-blue-600/10" : ""}
                          >
                            <td className="font-mono">{device.device_key}</td>
                            <td>
                              {device.activation_code ? (
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-green-400">{device.activation_code}</span>
                                  <button 
                                    onClick={() => copyToClipboard(device.activation_code)}
                                    className="text-zinc-500 hover:text-white"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-zinc-600">—</span>
                              )}
                            </td>
                            <td>{getStatusBadge(device)}</td>
                            <td>
                              {device.is_paid ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-zinc-600" />
                              )}
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => viewDeviceDetails(device.device_key)}
                                  className="text-zinc-400 hover:text-white"
                                  data-testid={`view-device-${device.device_key}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {!device.activation_code && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => activateDevice(device.device_key)}
                                    className="text-blue-400 hover:text-blue-300"
                                    data-testid={`activate-device-${device.device_key}`}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredDevices.length === 0 && (
                      <div className="text-center py-8 text-zinc-500">
                        No devices found
                      </div>
                    )}
                  </div>
                </div>

                {/* Device Details Panel */}
                <div>
                  {selectedDevice ? (
                    <Card className="bg-[#0F0F13] border-white/5" data-testid="device-details-panel">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center justify-between">
                          Device Details
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedDevice(null)}
                            className="text-zinc-500"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-zinc-500 text-sm">Device Key</p>
                          <p className="font-mono text-lg">{selectedDevice.device_key}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-sm">Activation Code</p>
                          <p className="font-mono text-lg text-green-400">
                            {selectedDevice.activation_code || "Not generated"}
                          </p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-sm">Status</p>
                          <div className="mt-1">{getStatusBadge(selectedDevice)}</div>
                        </div>
                        {selectedDevice.trial_end && (
                          <div>
                            <p className="text-zinc-500 text-sm">Trial Ends</p>
                            <p>{new Date(selectedDevice.trial_end).toLocaleString()}</p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="pt-4 space-y-2">
                          {!selectedDevice.activation_code && (
                            <Button
                              onClick={() => activateDevice(selectedDevice.device_key)}
                              className="w-full bg-blue-600 hover:bg-blue-500"
                              data-testid="generate-code-button"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" /> Generate Activation Code
                            </Button>
                          )}
                          {!selectedDevice.is_paid && (
                            <Button
                              onClick={() => markAsPaid(selectedDevice.device_key)}
                              className="w-full bg-green-600 hover:bg-green-500"
                              data-testid="mark-paid-button"
                            >
                              <DollarSign className="w-4 h-4 mr-2" /> Mark as Paid
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              setPlaylistForm({ ...playlistForm, device_key: selectedDevice.device_key });
                              setShowPlaylistModal(true);
                            }}
                            variant="outline"
                            className="w-full border-white/10"
                            data-testid="add-playlist-button"
                          >
                            <Plus className="w-4 h-4 mr-2" /> Add Playlist
                          </Button>
                          <Button
                            onClick={() => deleteDevice(selectedDevice.device_key)}
                            variant="outline"
                            className="w-full border-red-500/30 text-red-500 hover:bg-red-500/10"
                            data-testid="delete-device-button"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete Device
                          </Button>
                        </div>

                        {/* Playlists */}
                        {selectedDevice.playlists?.length > 0 && (
                          <div className="pt-4 border-t border-white/5">
                            <p className="text-zinc-500 text-sm mb-2">Playlists ({selectedDevice.playlists.length})</p>
                            <div className="space-y-2">
                              {selectedDevice.playlists.map((playlist) => (
                                <div key={playlist.id} className="flex items-center justify-between p-2 bg-black/30 rounded-lg">
                                  <div>
                                    <p className="text-sm font-medium">{playlist.name}</p>
                                    <p className="text-xs text-zinc-500">{playlist.playlist_type.toUpperCase()}</p>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deletePlaylist(playlist.id)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-[#0F0F13] border-white/5">
                      <CardContent className="py-12 text-center text-zinc-500">
                        <MonitorSmartphone className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Select a device to view details</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Playlists Tab */}
          {activeTab === "broadcasts" && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <p className="text-zinc-400">Send rolling ticker messages to users.</p>
                <button onClick={() => setShowTickerModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-white text-sm" data-testid="new-broadcast-button">+ New Broadcast</button>
              </div>
              <div className="space-y-3">
                {tickers.length === 0 ? (
                  <div className="bg-[#0F0F13] rounded-xl border border-white/5 p-8 text-center">
                    <p className="text-zinc-500">No broadcast messages yet</p>
                  </div>
                ) : tickers.map(t => (
                  <div key={t.id} className={"bg-[#0F0F13] rounded-xl border p-4 " + (t.is_active ? "border-cyan-500/30" : "border-white/5 opacity-60")}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm mb-2">{t.message}</p>
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className={"px-2 py-0.5 rounded-full text-xs font-semibold uppercase " + (t.is_active ? "bg-green-500/20 text-green-400" : "bg-zinc-500/20 text-zinc-400")}>{t.is_active ? "Active" : "Paused"}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase bg-blue-500/20 text-blue-400">{t.target_audience}</span>
                          <span className="text-xs text-zinc-500">Priority: {t.priority}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={async () => { await axios.put(`${API}/admin/ticker/${t.id}`, { is_active: !t.is_active }, { headers: getAuthHeader() }); fetchTickers(); }} className={"px-3 py-1.5 rounded text-xs font-medium " + (t.is_active ? "bg-yellow-500/10 text-yellow-400" : "bg-green-500/10 text-green-400")}>{t.is_active ? "Pause" : "Activate"}</button>
                        <button onClick={async () => { await axios.delete(`${API}/admin/ticker/${t.id}`, { headers: getAuthHeader() }); fetchTickers(); }} className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20">✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {showTickerModal && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                  <div className="bg-[#0F0F13] border border-white/10 rounded-xl p-6 w-full max-w-md">
                    <h3 className="text-lg font-medium text-white mb-4">New Broadcast Message</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-zinc-400 text-sm">Message</label>
                        <textarea value={tickerForm.message} onChange={e => setTickerForm({...tickerForm, message: e.target.value})} className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/10 rounded text-white min-h-[80px]" placeholder="Your message..." data-testid="ticker-message-input" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-sm">Target Audience</label>
                        <select value={tickerForm.target_audience} onChange={e => setTickerForm({...tickerForm, target_audience: e.target.value})} className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/10 rounded text-white" data-testid="ticker-audience-select">
                          <option value="all">All Users</option>
                          <option value="trial_only">Trial Users Only</option>
                          <option value="expiring_soon">Expiring Soon</option>
                          <option value="expired">Expired Users</option>
                          <option value="paid">Paid Users</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-zinc-400 text-sm">Priority (0-100)</label>
                        <input type="number" min="0" max="100" value={tickerForm.priority} onChange={e => setTickerForm({...tickerForm, priority: parseInt(e.target.value)||0})} className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/10 rounded text-white" />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button onClick={() => setShowTickerModal(false)} className="flex-1 px-4 py-2 border border-white/10 rounded text-white/60 hover:text-white">Cancel</button>
                        <button onClick={async () => { await axios.post(`${API}/admin/ticker`, {...tickerForm, expires_at: tickerForm.expires_at||null}, { headers: getAuthHeader() }); setShowTickerModal(false); setTickerForm({ message: "", target_audience: "all", priority: 0, is_active: true, expires_at: "" }); fetchTickers(); }} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-white" data-testid="save-broadcast-button">Create</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "content" && (
            <div className="animate-fade-in">
              <AdminContentTab authHeader={getAuthHeader()} />
            </div>
          )}
          {activeTab === "playlists" && (
            <div className="space-y-6 animate-fade-in">
              <div className="flex items-center justify-between">
                <p className="text-zinc-400">
                  Manage playlists for devices. Select a device first to add playlists.
                </p>
                <Button
                  onClick={() => setShowPlaylistModal(true)}
                  className="bg-blue-600 hover:bg-blue-500"
                  data-testid="new-playlist-button"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Playlist
                </Button>
              </div>

              <div className="bg-[#0F0F13] rounded-xl border border-white/5 p-8 text-center">
                <ListVideo className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-500">
                  Go to Devices tab and select a device to manage its playlists
                </p>
                <Button
                  onClick={() => setActiveTab("devices")}
                  variant="outline"
                  className="mt-4 border-white/10"
                >
                  Go to Devices
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Add Playlist Modal */}
      <Dialog open={showPlaylistModal} onOpenChange={setShowPlaylistModal}>
        <DialogContent className="bg-[#0F0F13] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add Playlist</DialogTitle>
          </DialogHeader>
          <form onSubmit={createPlaylist} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Device Key</Label>
              <Input
                value={playlistForm.device_key}
                onChange={(e) => setPlaylistForm({ ...playlistForm, device_key: e.target.value })}
                className="mt-1 bg-black/50 border-white/10 text-white font-mono"
                placeholder="ABC123"
                required
                data-testid="playlist-device-key-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Playlist Name</Label>
              <Input
                value={playlistForm.name}
                onChange={(e) => setPlaylistForm({ ...playlistForm, name: e.target.value })}
                className="mt-1 bg-black/50 border-white/10 text-white"
                placeholder="My IPTV"
                required
                data-testid="playlist-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Type</Label>
              <select
                value={playlistForm.playlist_type}
                onChange={(e) => setPlaylistForm({ ...playlistForm, playlist_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-black/50 border border-white/10 rounded-md text-white"
                data-testid="playlist-type-select"
              >
                <option value="xtream">Xtream Codes</option>
                <option value="m3u">M3U URL</option>
              </select>
            </div>

            {playlistForm.playlist_type === "m3u" && (
              <div>
                <Label className="text-zinc-400">M3U URL</Label>
                <Input
                  value={playlistForm.m3u_url}
                  onChange={(e) => setPlaylistForm({ ...playlistForm, m3u_url: e.target.value })}
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  placeholder="http://example.com/playlist.m3u"
                  data-testid="m3u-url-input"
                />
              </div>
            )}

            {playlistForm.playlist_type === "xtream" && (
              <>
                <div>
                  <Label className="text-zinc-400">Server URL</Label>
                  <Input
                    value={playlistForm.xtream_url}
                    onChange={(e) => setPlaylistForm({ ...playlistForm, xtream_url: e.target.value })}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                    placeholder="http://example.com:8080"
                    data-testid="xtream-url-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Username</Label>
                  <Input
                    value={playlistForm.xtream_username}
                    onChange={(e) => setPlaylistForm({ ...playlistForm, xtream_username: e.target.value })}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                    placeholder="username"
                    data-testid="xtream-username-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Password</Label>
                  <Input
                    value={playlistForm.xtream_password}
                    onChange={(e) => setPlaylistForm({ ...playlistForm, xtream_password: e.target.value })}
                    className="mt-1 bg-black/50 border-white/10 text-white"
                    placeholder="password"
                    data-testid="xtream-password-input"
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPlaylistModal(false)}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-500"
                data-testid="save-playlist-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Playlist
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
